"use client";

import { useMemo, useState } from 'react'
import { shellLinkClick } from '@/lib/clientNav'
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts'
import { useFinance, useTagPolicy, latestMonth } from '@/lib/finance/finance-data-context'
import { aggregateAccounts } from '@/lib/finance/display'
import { balanceAsOf } from '@/lib/finance/asof'
import { dateToMs, fmtDate, fmtMsDate, fmtUsd } from '@/lib/finance/format'
import { accountGroupColor, accountGroupOf } from '@/lib/finance/categories'
import { Card, Money, TagBadges, TxnListItem } from '@/components/finance/ui'
import { usePageRange, rangeLabel } from '@/components/finance/RangePicker'
import { ChartTooltip, crosshair } from '@/components/finance/ChartTooltip'
import { timeXAxisProps, timeTickFormatter, yAxisProps, gridProps, CHART_MARGIN, ACTIVE_DOT, downsample } from '@/components/finance/chartDefaults'

const TXN_PAGE = 50

export function AccountDetail({ accountId }: { accountId: string }) {
  const { accounts: rawAccounts, networth, txns } = useFinance()
  const { policy: tagPolicy } = useTagPolicy()
  const [txnLimit, setTxnLimit] = useState(TXN_PAGE)

  // Aggregate the calculated 401k halves so a combined-401k id from the
  // Accounts list resolves to one record here too.
  const accounts = useMemo(() => aggregateAccounts(rawAccounts), [rawAccounts])
  const account = accounts.find((a) => a.id === accountId)

  const last = latestMonth(networth.months)
  const { startMonth: windowStart, endMonth: windowEnd, state: rangeState, control } = usePageRange(
    networth.months[0], last,
  )

  // Liabilities are stored negative; plot their magnitude so the balance line
  // reads positive like every other account (net-worth math is unaffected).
  // Plot the account's OWN dated snapshots (daily points as they come in), not
  // the global net-worth dates, so the line shows real observations.
  const history = useMemo(() => {
    if (!account) return []
    const sign = account.isLiability ? -1 : 1
    return downsample(
      Object.keys(account.balances)
        .filter((d) => d >= windowStart)
        .sort(),
      (d) => account.balances[d],
    ).map((d) => ({ month: d, t: dateToMs(d), balance: account.balances[d]! * sign }))
  }, [account, windowStart])

  // The account's transactions. Prefer the resolved FK (accountId) for a
  // precise match; fall back to the old normalized contains test on the raw
  // label for rows that aren't linked to an account entity.
  //
  // Aggregated 401k plan: the plan's raw transactions link to its BASE account
  // (e.g. vanguard|jeff|google-401k) while this card is a synthetic aggregate of
  // the calculated Roth/Traditional halves. Gather across the whole plan family
  // — base + halves + retired sub-accounts — so the plan's contributions/
  // dividends show here, with the Roth/Traditional split kept as the balance
  // (tax-insight) view above.
  const accountTxns = useMemo(() => {
    if (!account || !txns) return []
    if (account.id.includes('(aggregated)')) {
      const familyIds = new Set(
        rawAccounts
          .filter(
            (a) =>
              a.institution === account.institution &&
              a.owner === account.owner &&
              (a.name === account.name || a.name.startsWith(`${account.name} - `)),
          )
          .map((a) => a.id),
      )
      return txns.filter((t) => familyIds.has(t.accountId))
    }
    return txns.filter((t) => t.accountId === account.id)
  }, [account, txns, rawAccounts])

  if (!account) {
    return (
      <div className="space-y-4">
        <a href="/finance/accounts" onClick={shellLinkClick} className="text-xs font-medium text-accent-ink hover:underline">
          ← Accounts
        </a>
        <Card>
          <p className="py-6 text-center text-sm text-ink-3">Account not found.</p>
        </Card>
      </div>
    )
  }

  const color = accountGroupColor(accountGroupOf(account.type))
  const current = balanceAsOf(account, last)
  // Raw (signed) balance at the window start — history[] is sign-flipped for
  // the chart, so read the underlying value for the change math.
  const windowStartBal = history.length ? account.balances[history[0].month] : undefined
  const change = current !== undefined && windowStartBal !== undefined ? current - windowStartBal : undefined

  return (
    <div className="space-y-4">
      <a href="/finance/accounts" onClick={shellLinkClick} className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-ink hover:underline">
        ← Accounts
      </a>

      <Card>
        <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{account.name}</h1>
            <p className="mt-0.5 text-xs text-ink-3">
              {account.institution} · {account.owner} · {account.type}
              {account.asOf && ` · updated ${fmtDate(account.asOf)}`}
            </p>
            <div className="mt-2">
              <span className="text-3xl font-semibold tracking-tight">
                {current !== undefined ? fmtUsd(current) : '—'}
              </span>
              {change !== undefined && change !== 0 && (
                <span className="ml-3 text-sm">
                  <Money value={change} compact />{' '}
                  <span className="text-ink-3">{rangeLabel(rangeState)}</span>
                </span>
              )}
            </div>
          </div>
          {control}
        </div>

        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history} margin={CHART_MARGIN}>
              <CartesianGrid {...gridProps} />
              <XAxis {...timeXAxisProps} tickFormatter={timeTickFormatter(windowStart, windowEnd)} minTickGap={48} />
              <YAxis {...yAxisProps} domain={['auto', 'auto']} />
              <Tooltip cursor={crosshair} content={<ChartTooltip labelFormatter={fmtMsDate} />} />
              <Area     type="linear"
                dataKey="balance"
                name="Balance"
                stroke={color}
                strokeWidth={2}
                fill={color}
                fillOpacity={0.1}
                dot={false}
                activeDot={ACTIVE_DOT}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card
        title="Transactions"
        subtitle={txns === null ? undefined : `${accountTxns.length.toLocaleString()} on this account`}
      >
        {txns === null ? (
          <p className="py-4 text-center text-sm text-ink-3">Loading transactions…</p>
        ) : accountTxns.length === 0 ? (
          <p className="py-4 text-center text-sm text-ink-3">
            No transactions recorded for this account. (Investment and property accounts
            are balance-tracked without a transaction feed.)
          </p>
        ) : (
          <>
            <ul className="divide-y divide-edge">
              {accountTxns.slice(0, txnLimit).map((t, i) => (
                <TxnListItem key={`${t.date}-${i}`} t={t} badge={<TagBadges tags={t.tags} policy={tagPolicy} />} />
              ))}
            </ul>
            {accountTxns.length > txnLimit && (
              <button
                onClick={() => setTxnLimit((n) => n + TXN_PAGE)}
                className="mt-3 w-full rounded-lg border border-edge py-2 text-xs font-medium text-ink-2 hover:bg-surface-3"
              >
                Show more ({(accountTxns.length - txnLimit).toLocaleString()} more)
              </button>
            )}
          </>
        )}
      </Card>
    </div>
  )
}
